import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fromJS, List, Map } from 'immutable';
import { extend, get } from 'lodash';
import { CustomValidators } from 'ngx-custom-validators';
import { v4 } from 'uuid';
import {
  DepositTypesEnum,
  DocumentTypesEnum,
} from '../../../document-data/document-data.data';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { DepositPayment } from '../../../document-data/sub-models/deposit-payment.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import {
  IFormFieldErrorMessages,
  IFormMasterBuilder,
  IFormValidators,
} from '../../../form-master/form-master.interface';
import { MoneyPipe } from '../../../shared/pipes/money/money.pipe';
import { CalculatorService } from '../../../shared/services/calculator/calculator.service';
import { DateTimeService } from 'i2g-ng-utils';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';
import {
  DepositRequestTabsEnum,
  IDepositRequestTab,
} from './deposit-request-dialog.data';
import { SettingsContent } from '../../../settings-data/sub-models/settings-content.model';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { DepositToggleValue } from '../../../settings-data/sub-models/company-settings/deposit-toggle-value.model';

@Injectable()
export class DepositRequestDialogBuilder implements IFormMasterBuilder {
  FORM_KEYS = {
    PERCENTAGE_PERCENT: 'percentagePercent',
    PERCENTAGE_FIXED_AMOUNT: 'percentageFixedAmount',
    FIXED_AMOUNT: 'fixedAmount',
    DUE_DATE: 'dueDate',
    ADD_DEPOSIT_FOR_FUTURE: 'addDepositForFuture',
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _calculatorService: CalculatorService,
    private _dateTimeService: DateTimeService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _moneyPipe: MoneyPipe,
    private _translateService: TranslateService
  ) {}

  buildTabs(
    currencySymbol: string,
    trackingScreenName: TrackingScreenNamesEnum
  ): List<IDepositRequestTab> {
    return List([
      {
        key: this._translateService.instant(
          'DIALOG.DEPOSIT_REQUEST.PERCENT_TYPE'
        ),
        value: DepositRequestTabsEnum.percent,
        trackingAction: this._trackingActionBuilder.buildTabTapped(
          'percentage',
          trackingScreenName
        ),
      },
      {
        key: this._translateService.instant(
          'DIALOG.DEPOSIT_REQUEST.FIXED_TYPE',
          {
            currencySymbol,
          }
        ),
        value: DepositRequestTabsEnum.fixed,
        trackingAction: this._trackingActionBuilder.buildTabTapped(
          'fixed',
          trackingScreenName
        ),
      },
    ]);
  }

  buildForm(
    deposit: Deposit,
    outstandingBalance: number,
    depositPayment: DepositPayment,
    addDepositForFuture: boolean
  ): FormGroup {
    const formKeys = this.FORM_KEYS;
    const depositAmountPaid: number = get(
      depositPayment,
      DepositPayment.KEYS.AMOUNT_PAID,
      0
    );
    const depositTotalAmount: number = get(
      depositPayment,
      DepositPayment.KEYS.TOTAL_AMOUNT,
      deposit.fixedAmount
    );
    const depositPercentage: number = get(
      depositPayment,
      DepositPayment.KEYS.PERCENTAGE,
      deposit.percentage
    );
    const maxAmount: number = outstandingBalance + depositAmountPaid;
    const validators: IFormValidators = this.buildValidators(maxAmount);

    return this._formBuilder.group({
      [formKeys.DUE_DATE]: [deposit.dueDate, Validators.required],
      [formKeys.FIXED_AMOUNT]: [
        depositTotalAmount,
        validators[formKeys.FIXED_AMOUNT],
      ],
      [formKeys.PERCENTAGE_PERCENT]: [
        depositPercentage > 100 ? 0 : depositPercentage,
        validators[formKeys.PERCENTAGE_PERCENT],
      ],
      [formKeys.PERCENTAGE_FIXED_AMOUNT]: [
        depositTotalAmount,
        validators[formKeys.PERCENTAGE_FIXED_AMOUNT],
      ],
      [formKeys.ADD_DEPOSIT_FOR_FUTURE]: [addDepositForFuture],
    });
  }

  buildValidators(maxAmount: number): IFormValidators {
    const formKeys = this.FORM_KEYS;

    return {
      [formKeys.FIXED_AMOUNT]: [
        CustomValidators.number,
        CustomValidators.min(0),
        CustomValidators.max(maxAmount),
      ],
      [formKeys.PERCENTAGE_PERCENT]: [
        CustomValidators.number,
        CustomValidators.range([0, 100]),
      ],
      [formKeys.PERCENTAGE_FIXED_AMOUNT]: [
        CustomValidators.number,
        CustomValidators.min(0),
        CustomValidators.max(maxAmount),
      ],
    };
  }

  buildDepositRequest(
    depositId: string,
    depositType: DepositTypesEnum,
    depositRequestInfo: Map<string, any>
  ) {
    const formKeys = this.FORM_KEYS;

    return new Deposit({
      [Deposit.KEYS.ID]: depositId || v4(),
      [Deposit.KEYS.DUE_DATE]: depositRequestInfo.get(formKeys.DUE_DATE),
      [Deposit.KEYS.FIXED_AMOUNT]: depositRequestInfo.get(
        depositType === DepositTypesEnum.fixed_amount
          ? formKeys.FIXED_AMOUNT
          : formKeys.PERCENTAGE_FIXED_AMOUNT
      ),
      [Deposit.KEYS.PERCENTAGE]: depositRequestInfo.get(
        formKeys.PERCENTAGE_PERCENT
      ),
      [Deposit.KEYS.TYPE]: depositType,
    });
  }

  buildNewDeposit(
    calculationResult: CalculationResult,
    documentDueDate: string,
    defaultPercentage: number = 50
  ) {
    const depositProps = {
      [Deposit.KEYS.PERCENTAGE]: defaultPercentage,
      [Deposit.KEYS.TYPE]: DepositTypesEnum.percentage,
    };
    const deposit: Deposit = new Deposit(depositProps);
    const depositPayment: DepositPayment = this._calculatorService.calculateDepositAmountAndPercentage(
      deposit,
      calculationResult
    );
    const today = this._dateTimeService.formatDate();
    const docDueDateNumOfDaysFromToday = this._dateTimeService.calculateNumberOfUnitsFromDate(
      documentDueDate,
      today
    );

    return new Deposit(
      extend(depositProps, {
        [Deposit.KEYS.FIXED_AMOUNT]: depositPayment.totalAmount,
        [Deposit.KEYS.DUE_DATE]:
          docDueDateNumOfDaysFromToday > 0 ? today : documentDueDate,
      })
    );
  }

  buildDepositToggleProps(
    documentType: DocumentTypesEnum,
    addDepositForFuture: boolean,
    depositPercentage: number
  ): Map<string, any> {
    return fromJS({
      [SettingsContent.KEYS.COMPANY_SETTINGS]: {
        [CompanySettings.KEYS.DEPOSIT_TOGGLE]: {
          [documentType]: {
            [DepositToggleValue.KEYS.VALUE]: depositPercentage,
            [DepositToggleValue.KEYS.TOGGLE_STATE]: addDepositForFuture,
          },
        },
      },
    });
  }

  buildErrorMessages(
    documentBalance: number,
    documentCurrencyCode: string,
    documentLocale: string
  ): { amount: IFormFieldErrorMessages; percentage: IFormFieldErrorMessages } {
    return {
      amount: {
        min: {
          message: 'DIALOG.DEPOSIT_REQUEST.ERROR.AMOUNT.MIN',
          translateParams: {
            minAmount: this._moneyPipe.transform(
              0,
              documentCurrencyCode,
              documentLocale
            ) as string,
          },
        },
        max: {
          message: 'DIALOG.DEPOSIT_REQUEST.ERROR.AMOUNT.MAX',
          translateParams: {
            documentBalance: this._moneyPipe.transform(
              documentBalance,
              documentCurrencyCode,
              documentLocale
            ) as string,
          },
        },
      },
      percentage: {
        range: {
          message: 'DIALOG.DEPOSIT_REQUEST.ERROR.PERCENT.RANGE',
        },
      },
    };
  }
}
